/**
 * FilterDropdownContainer - Logic for filter dropdown
 *
 * Manages filter label calculation and owns the controlled open state for the
 * shared `SelectDropdown` surface (SE-5570). The gluestack uncontrolled state
 * machine (and the SE-3823 `setTimeout` defer that worked around its
 * closeOnSelect desync) are gone — the shell owns open/close, so selection
 * calls `onSelect` directly.
 * @remarks This is a Container component - contains logic and callbacks
 */

import { memo, useCallback, useMemo, useState } from "react";

import type { PerformanceFilter } from "@/features/player-detail/types";
import FilterDropdownView from "./FilterDropdownView";

/**
 * Filter configuration
 */
interface FilterConfig {
  readonly id: PerformanceFilter;
  readonly label: string;
}

/**
 * Props for FilterDropdownContainer component
 */
interface FilterDropdownProps {
  readonly isDark: boolean;
  readonly activeFilter: PerformanceFilter;
  readonly onSelect: (filter: PerformanceFilter) => void;
}

const FILTERS: readonly FilterConfig[] = [
  { id: "last10", label: "Last 10 games" },
  { id: "last1000min", label: "Last 1000 minutes" },
  { id: "last60days", label: "Last 60 days" },
] as const;

/**
 * Get active filter label
 * @param filter - Active filter ID
 */
function getFilterLabel(filter: PerformanceFilter): string {
  return FILTERS.find(f => f.id === filter)?.label ?? "Last 10 games";
}

/**
 * FilterDropdownContainer manages filter selection
 * @param props - Container props
 * @param props.isDark - Whether dark theme is active
 * @param props.activeFilter - Currently selected filter
 * @param props.onSelect - Callback when filter is selected
 * @returns FilterDropdownView component
 */
const FilterDropdownContainer = memo(
  ({ isDark, activeFilter, onSelect }: FilterDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
    const onClose = useCallback(() => setIsOpen(false), []);

    const filterLabel = useMemo(
      () => getFilterLabel(activeFilter),
      [activeFilter]
    );

    return (
      <FilterDropdownView
        isDark={isDark}
        activeFilter={activeFilter}
        filterLabel={filterLabel}
        onSelectFilter={onSelect}
        isOpen={isOpen}
        onToggle={onToggle}
        onClose={onClose}
      />
    );
  }
);

FilterDropdownContainer.displayName = "FilterDropdownContainer";

export default FilterDropdownContainer;
